<template>
  <div :key="componentKey">
    <DetailToolbar
      v-if="!inModal"
      :isNew="isNew"
      :editMode="editMode"
      :isAnyFieldDirty="isAnyFieldDirty"
      :onGoToList="goToList"
      :onToggleEditMode="toggleEditMode"
      :onSubmit="submitForm"
      :onConfirmResetChanges="confirmResetChanges"
      :onConfirmDelete="confirmDelete"
      :headerText="editData?.name"
    />
    <h3 class="pt-8">{{ t('text.masterData') }}</h3>
    <form>
      <ContractBaseData
        :id="id"
        :editData="effectiveEditData"
        :isNew="isNew"
        :editMode="editMode"
        :customers="customers"
        :contractTypes="contractTypes"
        :customerGroups="customerGroups"
        :handleDirtyState="handleDirtyState"
        :initialData="props.initialData"
        :disabledFields="props.disabledFields"
        :fetchData="fetchData"
        :handleValidateAreas="handleValidateAreas"
      />
      <DetailFormfooter
        :isNew="isNew"
        :editMode="editMode"
        :saveButtonLabel="t('buttons.save') + ' ' + t('labels.contract').toLocaleLowerCase()"
        :isAnyFieldDirty="isAnyFieldDirty"
        :onGoToList="goToList"
        :onToggleEditMode="toggleEditMode"
        :onSubmit="submitForm"
        :onConfirmResetChanges="confirmResetChanges"
      />
    </form>
    <h3 v-if="!isNew" class="pt-8">{{ t('text.deadlines') }}</h3>
    <SectionWrapper v-if="!isNew" class="viewmode">
      <SimpleDatePicker name="actionStart" :modelValue="contract?.actionStart" :disabled="true" />
      <div class="flex">
        <SimpleDatePicker
          name="supposedActionEnd"
          :modelValue="contract?.supposedActionEnd"
          :disabled="true"
        />
        <SimpleDatePicker
          name="definiteActionEnd"
          :modelValue="contract?.definiteActionEnd"
          :disabled="true"
        />
      </div>
    </SectionWrapper>
    <h3 v-if="!isNew && !inModal" class="pt-8">{{ t('text.relatedData') }}</h3>
    <ContractRelations
      v-if="!isNew && contract && !inModal"
      :contract="contract"
      :fetch="fetchData"
    />
    <TrackRecordingTable v-if="id && !inModal" :id="id" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import type {
  DetailFormDisableProps,
  DetailFormExtraProps,
} from '@/components/details/detailTypes';
import {
  DetailNavigationInlinePropsDefaults,
  useDetailNavigation,
  type UseDetailNavigationInlineProps,
} from '@/components/details/hooks/useDetailNavigation';
import type { SelectModel } from '@/components/forms/FormSelect.vue';
import { useFormDirtyState } from '@/components/forms/hooks/useFormDirtyState';
import { useFormPrefill } from '@/components/forms/hooks/useFormPrefill';
import { useDetailView } from '@/composables/useDetailView';
import { toSelectModel } from '@/utils/conversion';
import type { ContractV1Dto, CustomerV1Dto } from '@/openapi';
import { ContractingPartyToContractV1Dto } from '@/openapi';

import { useCustomerGroups } from '../customergroups/hooks/useCustomergroups';
import { useCustomers } from '../customers/hooks/useCustomers';

import ContractBaseData from './components/ContractBaseData.vue';
import ContractRelations from './components/ContractRelations.vue';
import { getContractSchema, initialContractValues } from './configs/contractFormConfig';
import {
  useContracts,
  useContractsConverter,
  useContractsValidation,
  useContractTypes,
} from './hooks/useContracts';
import type { AreaWithPartnerAndCaretaker, ContractSimple } from './contractsTypes';

const { t } = useI18n();
const props = withDefaults(
  defineProps<UseDetailNavigationInlineProps & DetailFormExtraProps & DetailFormDisableProps>(),
  {
    ...DetailNavigationInlinePropsDefaults,
    initialData: undefined,
    placeholders: undefined,
    allDisabled: undefined,
    disabledFields: undefined,
    suggestions: undefined,
  },
);

// TODO: generalize for all detail pages
const emit = defineEmits<{
  (
    e: 'submitSuccess',
    payload: {
      id?: string;
      request?: ContractSimple;
      resp: ContractSimple;
    },
  ): void;
}>();

const { deleteContract, updateContract, createContract, fetchContract } = useContracts();
const { fetchCustomerGroups } = useCustomerGroups();
const { fetchCustomers } = useCustomers();
const { fetchContractTypes } = useContractTypes();
const {
  convertContractSimpleToCreateContractDto,
  convertContractSimpleToUpdateContractDto,
  convertContractDtoToContractSimple,
} = useContractsConverter();
const { validateContractAreas } = useContractsValidation();

const { id, isNew, editMode, goToList, toggleEditMode } = useDetailNavigation({
  uuidFromProp: computed(() => props.uuid), // override id
  isNewFromProp: computed(() => props.isNew), // override isNew
  editModeFromProp: computed(() => props.editMode), // override editMode
});

const { isAnyFieldDirty, handleDirtyState } = useFormDirtyState();

const contract = ref<ContractSimple>();
const contractTypes = ref<SelectModel[]>([]);
const customerGroups = ref<SelectModel[]>([]);
const customers = ref<CustomerV1Dto[]>([]);
const editData = ref<ContractSimple>();

const { handleSubmit, setValues, setFieldError, setFieldValue, resetForm, errors } =
  useForm<ContractSimple>({
    initialValues: initialContractValues,
    validateOnMount: false,
    validationSchema: getContractSchema(t),
  });

const { watchInitialData } = useFormPrefill<ContractSimple>({
  initialData: computed(() => props.initialData ?? undefined),
  setValues,
  resetForm: (args) => resetForm(args),
  isNew,
  isDirty: () => isAnyFieldDirty.value,
});

watchInitialData((merged) => {
  console.warn('[ContractDetail] PREFILL applied', { merged });

  contract.value = merged;
  editData.value = { ...merged };
});

const submitForm = handleSubmit(onSubmit);

const effectiveEditData = computed<ContractSimple | undefined>(() => editData.value);

function confirmResetChanges() {
  setValues({ ...editData.value });
}

async function confirmDelete() {
  await deleteContract(id.value);
  goToList();
}

function handleValidateAreas(areas: AreaWithPartnerAndCaretaker[]) {
  let result = true;
  validateContractAreas(areas, (field, message) => setFieldError(field as never, message), t);

  if (Object.keys(errors.value).length > 0) result = false;

  return result;
}

async function onSubmit(values: ContractSimple) {
  if (isNew.value) {
    const { areasWithPartnerAndCaretaker } = values;
    if (!handleValidateAreas(areasWithPartnerAndCaretaker)) return;
  }

  if (!isNew.value && String(id.value)) {
    await updateContract(String(id.value), convertContractSimpleToUpdateContractDto(values));
    toggleEditMode();
    await fetchData();
  } else {
    const resp: ContractV1Dto = await createContract(
      convertContractSimpleToCreateContractDto(values),
    );

    emit('submitSuccess', {
      id: resp.id,
      request: values,
      resp: useContractsConverter().convertContractDtoToContractSimple(resp),
    });

    goToList();
  }
}

const fetchLookups = async () => {
  customers.value = (await fetchCustomers()) ?? [];

  contractTypes.value = toSelectModel((await fetchContractTypes('Rental')) ?? []);
  customerGroups.value = toSelectModel((await fetchCustomerGroups()) ?? []);

  if (isNew.value) {
    //     /** sets always fixed vaules */
    setFieldValue(
      'customerRole',
      Object.values([ContractingPartyToContractV1Dto.role.PROPERTY_TENANT])[0],
    );
  }
};

const fetchData = async () => {
  if (!id.value) return;

  const data = await fetchContract(String(id.value));
  const simple = data && convertContractDtoToContractSimple(data);

  contract.value = simple;
  editData.value = { ...simple };

  if (simple) {
    resetForm({
      values: { ...simple },
    });
  }
};

const clearValues = () => {
  contract.value = undefined;
  editData.value = undefined;
  setValues({
    name: '',
    startDate: undefined,
    endDate: undefined,
    contractTypeId: undefined,
  });
};

const { componentKey } = useDetailView({
  fetchData,
  fetchLookups,
  clearValues,
});
</script>
